import { useState, useEffect, useCallback } from 'react';
import { PromptType } from './RefineTab';
import { PromptCard } from './PromptCard';
import { useAuth } from '../../context/AuthContext';

interface PromptSelectorProps {
  selectedPrompt: PromptType;
  customPrompt: string;
  onPromptChange: (prompt: PromptType) => void;
  onCustomPromptChange: (prompt: string) => void;
}

interface SavedPrompt {
  id: string;
  name: string;
  description: string | null;
  promptText: string;
}

const presetPrompts = [
  {
    type: 'enrich' as PromptType,
    name: 'Enrich My Notes',
    description: 'Add context, explanations, and insights from the source material',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    type: 'format' as PromptType,
    name: 'Format My Notes',
    description: 'Clean up, organize, and structure your notes professionally',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
      </svg>
    ),
  },
  {
    type: 'citation' as PromptType,
    name: 'Add Citations',
    description: 'Include proper references, page numbers, and a bibliography',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    type: 'custom' as PromptType,
    name: 'Custom Prompt',
    description: 'Write your own instructions for how to process your notes',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
];

export function PromptSelector({
  selectedPrompt,
  customPrompt,
  onPromptChange,
  onCustomPromptChange,
}: PromptSelectorProps) {
  const maxLength = 500;
  const charCount = customPrompt.length;
  const { user, session } = useAuth();
  const isAuthenticated = !!user && !!session;

  // Save prompt state
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Saved prompts library state
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [activeSavedPromptId, setActiveSavedPromptId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchSavedPrompts = useCallback(async () => {
    if (!isAuthenticated || !session?.access_token) return;

    try {
      const response = await fetch('/api/refine/prompts', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });
      if (!response.ok) return;

      const data = await response.json();
      const custom = (data.prompts || []).filter(
        (p: any) => !p.isPreset && p.promptText
      );
      setSavedPrompts(custom);
    } catch {
      // Silently fail - saved prompts are a nice-to-have
    }
  }, [isAuthenticated, session?.access_token]);

  useEffect(() => {
    fetchSavedPrompts();
  }, [fetchSavedPrompts]);

  const handleSavePrompt = async () => {
    if (!customPrompt.trim() || !promptName.trim()) {
      setSaveError('Please enter a name for your prompt');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const response = await fetch('/api/refine/prompts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          name: promptName.trim(),
          description: promptDescription.trim() || undefined,
          promptText: customPrompt.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save prompt');
      }

      setSaveSuccess(true);
      setShowSaveForm(false);
      setPromptName('');
      setPromptDescription('');

      // Refresh saved prompts list
      await fetchSavedPrompts();

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveForm(false);
    setPromptName('');
    setPromptDescription('');
    setSaveError(null);
  };

  const handleSelectSavedPrompt = (prompt: SavedPrompt) => {
    onPromptChange('custom');
    onCustomPromptChange(prompt.promptText);
    setActiveSavedPromptId(prompt.id);
  };

  const handleDeleteSavedPrompt = async (promptId: string) => {
    if (!session?.access_token) return;

    setDeletingId(promptId);
    try {
      const response = await fetch(`/api/refine/prompts/${promptId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete prompt');
      }

      // If the deleted prompt was active, clear the selection
      if (activeSavedPromptId === promptId) {
        setActiveSavedPromptId(null);
      }

      // Refresh the list
      await fetchSavedPrompts();
    } catch {
      // Could add error handling here, but keep it simple
    } finally {
      setDeletingId(null);
    }
  };

  // Clear active saved prompt when user manually edits the textarea
  const handleCustomPromptChange = (value: string) => {
    onCustomPromptChange(value.slice(0, maxLength));
    if (activeSavedPromptId) {
      const active = savedPrompts.find(p => p.id === activeSavedPromptId);
      if (active && value !== active.promptText) {
        setActiveSavedPromptId(null);
      }
    }
  };

  // Clear active saved prompt when switching to a preset
  const handlePromptChange = (type: PromptType) => {
    if (type !== 'custom') {
      setActiveSavedPromptId(null);
    }
    onPromptChange(type);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
          <span className="text-primary-400 font-semibold text-sm">2</span>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-surface-100">Choose a Refinement Style</h3>
          <p className="text-sm text-surface-400">Select how you want your notes processed</p>
        </div>
      </div>

      {/* Prompt Cards Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {presetPrompts.map((prompt) => (
          <PromptCard
            key={prompt.type}
            type={prompt.type}
            name={prompt.name}
            description={prompt.description}
            icon={prompt.icon}
            isSelected={selectedPrompt === prompt.type && (prompt.type !== 'custom' || !activeSavedPromptId)}
            onClick={() => handlePromptChange(prompt.type)}
          />
        ))}
      </div>

      {/* Saved Prompts Library */}
      {isAuthenticated && savedPrompts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-surface-300 flex items-center gap-2">
            <svg className="w-4 h-4 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Your Saved Prompts
          </h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {savedPrompts.map((prompt) => (
              <button
                key={prompt.id}
                onClick={() => handleSelectSavedPrompt(prompt)}
                className={`
                  relative group w-full p-3 rounded-lg text-left transition-all duration-200
                  ${activeSavedPromptId === prompt.id
                    ? 'bg-primary-500/20 border border-primary-500'
                    : 'bg-surface-800 border border-surface-700 hover:border-surface-600'
                  }
                `}
              >
                {/* Selected indicator */}
                {activeSavedPromptId === prompt.id && (
                  <div className="absolute top-2.5 right-2.5">
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Delete button */}
                {activeSavedPromptId !== prompt.id && (
                  <div
                    className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSavedPrompt(prompt.id);
                    }}
                  >
                    {deletingId === prompt.id ? (
                      <svg className="w-4 h-4 text-surface-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-surface-500 hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </div>
                )}

                <h4 className={`font-medium text-sm mb-1 pr-6 ${activeSavedPromptId === prompt.id ? 'text-primary-300' : 'text-surface-200'}`}>
                  {prompt.name}
                </h4>
                {prompt.description && (
                  <p className={`text-xs ${activeSavedPromptId === prompt.id ? 'text-surface-300' : 'text-surface-500'}`}>
                    {prompt.description}
                  </p>
                )}
                <p className={`text-xs mt-1.5 line-clamp-2 ${activeSavedPromptId === prompt.id ? 'text-surface-400' : 'text-surface-600'}`}>
                  {prompt.promptText}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Prompt Textarea */}
      {selectedPrompt === 'custom' && (
        <div className="animate-fade-in space-y-3">
          <label className="block text-sm font-medium text-surface-300">
            Your Custom Instructions
          </label>
          <textarea
            value={customPrompt}
            onChange={(e) => handleCustomPromptChange(e.target.value)}
            placeholder="Write your instructions here... For example: 'Summarize each chapter into key takeaways and action items'"
            className="w-full h-32 px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder:text-surface-500 resize-none focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
          />
          <div className="flex items-center justify-between text-xs">
            <p className="text-surface-500">
              Be specific about the format and style you want
            </p>
            <span className={`${charCount >= maxLength ? 'text-red-400' : 'text-surface-500'}`}>
              {charCount}/{maxLength}
            </span>
          </div>

          {/* Save Prompt Section - Only for authenticated users */}
          {isAuthenticated && customPrompt.trim().length > 0 && (
            <div className="pt-2 border-t border-surface-700">
              {saveSuccess && (
                <div className="mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Prompt saved successfully!
                </div>
              )}

              {!showSaveForm ? (
                <button
                  onClick={() => setShowSaveForm(true)}
                  className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save this prompt for later
                </button>
              ) : (
                <div className="space-y-3 p-4 bg-surface-800/50 rounded-lg border border-surface-600">
                  <div>
                    <label className="block text-xs font-medium text-surface-400 mb-1">
                      Prompt Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={promptName}
                      onChange={(e) => setPromptName(e.target.value.slice(0, 100))}
                      placeholder="e.g., Book Summary Template"
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-sm text-surface-100 placeholder:text-surface-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-surface-400 mb-1">
                      Description <span className="text-surface-500">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={promptDescription}
                      onChange={(e) => setPromptDescription(e.target.value.slice(0, 200))}
                      placeholder="A brief description of what this prompt does"
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-sm text-surface-100 placeholder:text-surface-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>

                  {saveError && (
                    <p className="text-sm text-red-400">{saveError}</p>
                  )}

                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSavePrompt}
                      disabled={isSaving || !promptName.trim()}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-surface-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        'Save Prompt'
                      )}
                    </button>
                    <button
                      onClick={handleCancelSave}
                      disabled={isSaving}
                      className="px-4 py-2 bg-surface-700 hover:bg-surface-600 text-surface-300 text-sm font-medium rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hint for non-authenticated users */}
          {!isAuthenticated && customPrompt.trim().length > 0 && (
            <p className="text-xs text-surface-500 pt-2 border-t border-surface-700">
              Sign in to save your custom prompts for future use.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
